import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getUserProfile } from '../services/firebase';

// Subscription tiers
export enum SubscriptionTier {
  FREE = 'free',
  PREMIUM = 'premium',
  PLATINUM = 'platinum'
}

// Admin-configurable limits
interface SubscriptionLimits {
  clickableProfilesInGrid: number; // How many profiles in grid are clickable
  messagesPerDay: number;
  canSeeWhoLikesYou: boolean;
  canUseAdvancedFilters: boolean;
  canBoostProfile: boolean;
  canUseIncognito: boolean;
  canSeeReadReceipts: boolean;
  
  // Profile Discovery Controls (granular permissions)
  canControlProfileVisibility: boolean; // Can set profile visibility (public/matches/private)
  canControlAppModeVisibility: boolean; // Can control who sees app mode preference
  canControlDistanceSharing: boolean; // Can control distance display
  canControlLastActiveStatus: boolean; // Can control last active visibility
  canControlReadReceiptSettings: boolean; // Can control read receipt settings
  canControlMessageSettings: boolean; // Can control who can message them
  canControlCruisingPrivacy: boolean; // Can control cruising visibility settings
  canControlHealthPrivacy: boolean; // Can control health data sharing
  canControlActivityPrivacy: boolean; // Can control activity data sharing
  canAccessAdvancedPrivacy: boolean; // Access to advanced privacy features
  
  // Cruise/Location Sharing Limits
  cruiseDurationMinutes: number; // Duration per cruise session
  cruiseSessionsPerPeriod: number; // Number of sessions allowed per period (-1 = unlimited)
  cruisePeriodType: 'day' | 'week'; // Reset period
  canCruiseUnlimited: boolean; // Override for unlimited cruising
  
  maxPhotos: number;
  canVideoCall: boolean;
  prioritySupport: boolean;
}

// Default limits configuration (admin-configurable)
const DEFAULT_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  [SubscriptionTier.FREE]: {
    clickableProfilesInGrid: 5, // First 5 profiles are clickable
    messagesPerDay: 5,
    canSeeWhoLikesYou: false,
    canUseAdvancedFilters: false,
    canBoostProfile: false,
    canUseIncognito: false,
    canSeeReadReceipts: false,
    
    // Profile Discovery Controls - Limited for Free
    canControlProfileVisibility: false, // Premium feature
    canControlAppModeVisibility: false, // Premium feature
    canControlDistanceSharing: false, // Premium feature
    canControlLastActiveStatus: false, // Premium feature
    canControlReadReceiptSettings: false, // Premium feature
    canControlMessageSettings: false, // Premium feature
    canControlCruisingPrivacy: false, // Premium feature
    canControlHealthPrivacy: false, // Premium feature
    canControlActivityPrivacy: false, // Premium feature
    canAccessAdvancedPrivacy: false, // Premium feature
    
    // Cruise limits for FREE - once per week for 30 minutes
    cruiseDurationMinutes: 30,
    cruiseSessionsPerPeriod: 1,
    cruisePeriodType: 'week',
    canCruiseUnlimited: false,
    
    maxPhotos: 3,
    canVideoCall: false,
    prioritySupport: false
  },
  [SubscriptionTier.PREMIUM]: {
    clickableProfilesInGrid: 25, // First 25 profiles are clickable
    messagesPerDay: 50,
    canSeeWhoLikesYou: true,
    canUseAdvancedFilters: true,
    canBoostProfile: false,
    canUseIncognito: true,
    canSeeReadReceipts: true,
    
    // Profile Discovery Controls - Most features for Premium
    canControlProfileVisibility: true, // Premium feature enabled
    canControlAppModeVisibility: true, // Premium feature enabled
    canControlDistanceSharing: true, // Premium feature enabled
    canControlLastActiveStatus: true, // Premium feature enabled
    canControlReadReceiptSettings: true, // Premium feature enabled
    canControlMessageSettings: true, // Premium feature enabled
    canControlCruisingPrivacy: true, // Premium feature enabled
    canControlHealthPrivacy: true, // Premium feature enabled
    canControlActivityPrivacy: true, // Premium feature enabled
    canAccessAdvancedPrivacy: false, // Platinum exclusive
    
    // Cruise limits for PREMIUM - once per day for 30 minutes
    cruiseDurationMinutes: 30,
    cruiseSessionsPerPeriod: 1,
    cruisePeriodType: 'day',
    canCruiseUnlimited: false,
    
    maxPhotos: 8,
    canVideoCall: true,
    prioritySupport: false
  },
  [SubscriptionTier.PLATINUM]: {
    clickableProfilesInGrid: -1, // All profiles are clickable
    messagesPerDay: -1, // unlimited
    canSeeWhoLikesYou: true,
    canUseAdvancedFilters: true,
    canBoostProfile: true,
    canUseIncognito: true,
    canSeeReadReceipts: true,
    
    // Profile Discovery Controls - All features for Platinum
    canControlProfileVisibility: true, // Premium feature enabled
    canControlAppModeVisibility: true, // Premium feature enabled
    canControlDistanceSharing: true, // Premium feature enabled
    canControlLastActiveStatus: true, // Premium feature enabled
    canControlReadReceiptSettings: true, // Premium feature enabled
    canControlMessageSettings: true, // Premium feature enabled
    canControlCruisingPrivacy: true, // Premium feature enabled
    canControlHealthPrivacy: true, // Premium feature enabled
    canControlActivityPrivacy: true, // Premium feature enabled
    canAccessAdvancedPrivacy: true, // Platinum exclusive
    
    // Cruise limits for PLATINUM - unlimited
    cruiseDurationMinutes: 30,
    cruiseSessionsPerPeriod: -1, // unlimited sessions
    cruisePeriodType: 'day',
    canCruiseUnlimited: true,
    
    maxPhotos: 20,
    canVideoCall: true,
    prioritySupport: true
  }
};

// Usage tracking - simplified since we don't track individual views
interface UsageData {
  messagesToday: number;
  lastResetDate: Date;
  
  // Cruise usage tracking
  cruiseSessionsThisPeriod: number;
  totalCruiseMinutesThisPeriod: number;
  lastCruiseResetDate: Date;
  currentCruiseSessionStart?: Date;
}

interface SubscriptionContextType {
  tier: SubscriptionTier;
  limits: SubscriptionLimits;
  usage: UsageData;
  isBetaUser: boolean;
  hasLifetimePremium: boolean;
  canClickProfile: (profileIndex: number) => boolean;
  canSendMessage: () => boolean;
  recordMessageSent: () => void;
  upgradeRequired: (feature: string) => boolean;
  getUpgradeMessage: (feature: string) => string;
  updateSubscription: (newTier: SubscriptionTier) => void;
  resetUsage: () => void;
  
  // Cruise-related functions
  canStartCruiseSession: () => boolean;
  recordCruiseSessionStart: () => void;
  recordCruiseSessionEnd: () => void;
  getRemainingCruiseTime: () => number;
  getCruiseSessionsRemaining: () => number;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
  initialTier?: SubscriptionTier;
  adminLimits?: Partial<Record<SubscriptionTier, Partial<SubscriptionLimits>>>;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ 
  children, 
  initialTier = SubscriptionTier.FREE,
  adminLimits = {}
}) => {
  const { user } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>(initialTier);
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [hasLifetimePremium, setHasLifetimePremium] = useState(false);
  const [usage, setUsage] = useState<UsageData>({
    messagesToday: 0,
    lastResetDate: new Date(),
    cruiseSessionsThisPeriod: 0,
    totalCruiseMinutesThisPeriod: 0,
    lastCruiseResetDate: new Date(),
    currentCruiseSessionStart: undefined
  });

  const userId = user?.uid || user?.sub;

  // Check beta user status and grant lifetime premium if applicable
  useEffect(() => {
    const checkBetaStatus = async () => {
      if (!userId) return;
      
      try {
        const profile = await getUserProfile(userId);
        if (profile?.isBetaUser) {
          setIsBetaUser(true);
          
          // If beta user has lifetime premium, upgrade their subscription
          if (profile.hasLifetimePremium) {
            setHasLifetimePremium(true);
            setTier(SubscriptionTier.PREMIUM);
          }
        }
      } catch (error) {
        console.error('Error checking beta status:', error);
      }
    };

    checkBetaStatus();
  }, [userId]);

  // Merge admin limits with defaults
  const limits: SubscriptionLimits = {
    ...DEFAULT_LIMITS[tier],
    ...adminLimits[tier]
  };

  // Reset usage counters at appropriate intervals
  useEffect(() => {
    const now = new Date();
    const lastReset = usage.lastResetDate;
    const lastCruiseReset = usage.lastCruiseResetDate;
    
    let shouldUpdateUsage = false;
    let updatedUsage = { ...usage };
    
    // Check if we need to reset daily counters (messages)
    if (now.getDate() !== lastReset.getDate() || 
        now.getMonth() !== lastReset.getMonth() || 
        now.getFullYear() !== lastReset.getFullYear()) {
      updatedUsage.messagesToday = 0;
      updatedUsage.lastResetDate = now;
      shouldUpdateUsage = true;
    }
    
    // Check if we need to reset cruise counters based on period type
    const needsCruiseReset = limits.cruisePeriodType === 'day' 
      ? (now.getDate() !== lastCruiseReset.getDate() || 
         now.getMonth() !== lastCruiseReset.getMonth() || 
         now.getFullYear() !== lastCruiseReset.getFullYear())
      : (Math.floor((now.getTime() - lastCruiseReset.getTime()) / (1000 * 60 * 60 * 24 * 7)) >= 1); // weekly
    
    if (needsCruiseReset) {
      updatedUsage.cruiseSessionsThisPeriod = 0;
      updatedUsage.totalCruiseMinutesThisPeriod = 0;
      updatedUsage.lastCruiseResetDate = now;
      updatedUsage.currentCruiseSessionStart = undefined;
      shouldUpdateUsage = true;
    }
    
    if (shouldUpdateUsage) {
      setUsage(updatedUsage);
    }
  }, [usage.lastResetDate, usage.lastCruiseResetDate, limits.cruisePeriodType]);

  const canClickProfile = (profileIndex: number): boolean => {
    if (limits.clickableProfilesInGrid === -1) return true; // unlimited
    return profileIndex < limits.clickableProfilesInGrid;
  };

  const canSendMessage = (): boolean => {
    if (limits.messagesPerDay === -1) return true; // unlimited
    return usage.messagesToday < limits.messagesPerDay;
  };


  const recordMessageSent = () => {
    setUsage(prev => ({
      ...prev,
      messagesToday: prev.messagesToday + 1
    }));
  };


  const upgradeRequired = (feature: string): boolean => {
    switch (feature) {
      case 'advancedFilters':
        return !limits.canUseAdvancedFilters;
      case 'whoLikesYou':
        return !limits.canSeeWhoLikesYou;
      case 'incognito':
        return !limits.canUseIncognito;
      case 'readReceipts':
        return !limits.canSeeReadReceipts;
      case 'videoCall':
        return !limits.canVideoCall;
      case 'profileBoost':
        return !limits.canBoostProfile;
      
      // Profile Discovery Features
      case 'profileVisibility':
        return !limits.canControlProfileVisibility;
      case 'appModeVisibility':
        return !limits.canControlAppModeVisibility;
      case 'distanceSharing':
        return !limits.canControlDistanceSharing;
      case 'lastActiveStatus':
        return !limits.canControlLastActiveStatus;
      case 'readReceiptSettings':
        return !limits.canControlReadReceiptSettings;
      case 'messageSettings':
        return !limits.canControlMessageSettings;
      case 'cruisingPrivacy':
        return !limits.canControlCruisingPrivacy;
      case 'healthPrivacy':
        return !limits.canControlHealthPrivacy;
      case 'activityPrivacy':
        return !limits.canControlActivityPrivacy;
      case 'advancedPrivacy':
        return !limits.canAccessAdvancedPrivacy;
      
      // Cruise feature
      case 'cruise':
        return !canStartCruiseSession();
      
      default:
        return false;
    }
  };

  const getUpgradeMessage = (feature: string): string => {
    const messages = {
      'moreProfiles': `You can only view the first ${limits.clickableProfilesInGrid} profiles with your current plan. Upgrade to Premium for ${DEFAULT_LIMITS[SubscriptionTier.PREMIUM].clickableProfilesInGrid} clickable profiles or Platinum for unlimited access!`,
      'messages': `You've reached your daily limit of ${limits.messagesPerDay} messages. Upgrade to send more messages!`,
      'advancedFilters': 'Advanced filters are available with Premium membership. Filter by position, tribes, and more!',
      'whoLikesYou': 'See who likes you with Premium membership. No more guessing games!',
      'incognito': 'Browse profiles privately with Premium membership. Stay invisible while exploring!',
      'readReceipts': 'See when your messages are read with Premium membership.',
      'videoCall': 'Video calls are available with Premium membership. Connect face-to-face!',
      'profileBoost': 'Boost your profile to the top with Platinum membership. Get more views!',
      'morePhotos': `Upload up to ${DEFAULT_LIMITS[SubscriptionTier.PREMIUM].maxPhotos} photos with Premium or ${DEFAULT_LIMITS[SubscriptionTier.PLATINUM].maxPhotos} with Platinum!`,
      
      // Profile Discovery Messages
      'profileVisibility': 'Control who can see your profile with Premium membership. Set to public, matches only, or private!',
      'appModeVisibility': 'Control who can see your dating preferences with Premium membership. Keep your intentions private!',
      'distanceSharing': 'Control distance display settings with Premium membership. Show or hide your distance from others!',
      'lastActiveStatus': 'Control your online status visibility with Premium membership. Stay private when you want!',
      'readReceiptSettings': 'Advanced read receipt controls available with Premium membership.',
      'messageSettings': 'Control who can message you with Premium membership. Filter out unwanted messages!',
      'cruisingPrivacy': 'Advanced cruising privacy controls available with Premium membership.',
      'healthPrivacy': 'Control health data sharing with Premium membership. Keep your health info private!',
      'activityPrivacy': 'Control activity data sharing with Premium membership. Manage your workout privacy!',
      'advancedPrivacy': 'Advanced privacy features are exclusive to Platinum membership. Get ultimate control!',
      
      // Cruise feature
      'cruise': tier === SubscriptionTier.FREE 
        ? `Free users can cruise for ${limits.cruiseDurationMinutes} minutes once per ${limits.cruisePeriodType}. Upgrade to Premium for daily cruising or Platinum for unlimited access!`
        : `Premium users get daily cruising sessions. Upgrade to Platinum for unlimited cruise time!`
    };
    
    return messages[feature as keyof typeof messages] || 'This feature requires a premium membership.';
  };

  const updateSubscription = (newTier: SubscriptionTier) => {
    // Don't allow subscription downgrades for beta users with lifetime premium
    if (hasLifetimePremium && newTier === SubscriptionTier.FREE) {
      return;
    }
    
    setTier(newTier);
    // In real app, this would sync with backend
    localStorage.setItem('subscriptionTier', newTier);
  };

  const resetUsage = () => {
    setUsage({
      messagesToday: 0,
      lastResetDate: new Date(),
      cruiseSessionsThisPeriod: 0,
      totalCruiseMinutesThisPeriod: 0,
      lastCruiseResetDate: new Date(),
      currentCruiseSessionStart: undefined
    });
  };

  // Cruise-related functions
  const canStartCruiseSession = (): boolean => {
    // Unlimited cruising for this tier
    if (limits.canCruiseUnlimited || limits.cruiseSessionsPerPeriod === -1) {
      return true;
    }
    
    // Check if user has cruise sessions remaining
    return usage.cruiseSessionsThisPeriod < limits.cruiseSessionsPerPeriod;
  };

  const recordCruiseSessionStart = () => {
    const now = new Date();
    setUsage(prev => ({
      ...prev,
      currentCruiseSessionStart: now
    }));
  };

  const recordCruiseSessionEnd = () => {
    const now = new Date();
    
    setUsage(prev => {
      if (!prev.currentCruiseSessionStart) {
        return prev; // No session was started
      }
      
      // Calculate session duration in minutes
      const sessionDuration = Math.floor((now.getTime() - prev.currentCruiseSessionStart.getTime()) / (1000 * 60));
      
      return {
        ...prev,
        cruiseSessionsThisPeriod: prev.cruiseSessionsThisPeriod + 1,
        totalCruiseMinutesThisPeriod: prev.totalCruiseMinutesThisPeriod + sessionDuration,
        currentCruiseSessionStart: undefined
      };
    });
  };

  const getRemainingCruiseTime = (): number => {
    if (limits.canCruiseUnlimited) {
      return -1; // Unlimited
    }
    
    if (!usage.currentCruiseSessionStart) {
      return limits.cruiseDurationMinutes; // Full session time available
    }
    
    // Calculate remaining time for current session
    const now = new Date();
    const elapsedMinutes = Math.floor((now.getTime() - usage.currentCruiseSessionStart.getTime()) / (1000 * 60));
    const remaining = limits.cruiseDurationMinutes - elapsedMinutes;
    
    return Math.max(0, remaining);
  };

  const getCruiseSessionsRemaining = (): number => {
    if (limits.canCruiseUnlimited || limits.cruiseSessionsPerPeriod === -1) {
      return -1; // Unlimited
    }
    
    return Math.max(0, limits.cruiseSessionsPerPeriod - usage.cruiseSessionsThisPeriod);
  };

  // Load subscription from localStorage on mount (but don't override beta users)
  useEffect(() => {
    if (hasLifetimePremium) return; // Don't override beta users with lifetime premium
    
    const stored = localStorage.getItem('subscriptionTier');
    if (stored && Object.values(SubscriptionTier).includes(stored as SubscriptionTier)) {
      setTier(stored as SubscriptionTier);
    }
  }, [hasLifetimePremium]);

  const value = {
    tier,
    limits,
    usage,
    isBetaUser,
    hasLifetimePremium,
    canClickProfile,
    canSendMessage,
    recordMessageSent,
    upgradeRequired,
    getUpgradeMessage,
    updateSubscription,
    resetUsage,
    canStartCruiseSession,
    recordCruiseSessionStart,
    recordCruiseSessionEnd,
    getRemainingCruiseTime,
    getCruiseSessionsRemaining
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;